import { useCallback, useEffect, useState } from 'react';

import { MIN_LEN } from '@/constants';
import { useAppDispatch, useAppSelector } from '@/store';
import { showSuccessToast } from '@/utils/toast.utils';
import { hideModal } from '@/store/thunks/ui/uiThunk';
import { selectIsManageAccessModal } from '@/store/selectors/ui';
import {
  selectManageAccessDraft,
  selectManageAccessExternalUsers,
  selectManageAccessInternalUsers,
  selectManageAccessSixId,
  selectManageAccessStep,
  selectManageAccessSubmitting,
} from '@/store/selectors/manageAccess';
import {
  resetManageAccessState,
  setManageAccessExternalUsers,
  setManageAccessInternalUsers,
  setManageAccessStep,
  setManageAccessSubmitting,
} from '@/store/slices/manageAccess/manageAccessSlice';
import { updateManageAccessThunk } from '@/store/thunks/manageAccess/updateManageAccessThunk';
import { createNewExternalUserThunk } from '@/store/thunks/user/createNewExternalUserThunk';
import { fetchCreateSixModalInformationThunk } from '@/store/thunks/create-six/fetchCreateSixModalInformation';
import {
  MANAGE_ACCESS_WIZARD_STEPS,
  resolveManageAccessWizardStepNumber,
  resolveManageAccessWizardStepStatus,
} from '@/utils/manageAccessWizard.utils';
import type { ManageAccessStep } from '@/types/manage-access.types';

function resolveNextStep(currentStep: ManageAccessStep): ManageAccessStep {
  if (currentStep === 'Owners') return 'Members';
  if (currentStep === 'Members') return 'External';
  return 'Review';
}

function resolvePreviousStep(currentStep: ManageAccessStep): ManageAccessStep {
  if (currentStep === 'Members') return 'Owners';
  if (currentStep === 'External') return 'Members';
  return 'External';
}

export function useManageAccessModal() {
  const dispatch = useAppDispatch();

  const isOpen = useAppSelector(selectIsManageAccessModal);
  const step = useAppSelector(selectManageAccessStep);
  const isSubmitting = useAppSelector(selectManageAccessSubmitting);
  const sixId = useAppSelector(selectManageAccessSixId);

  const draft = useAppSelector(selectManageAccessDraft);
  const internalUsers = useAppSelector(selectManageAccessInternalUsers);
  const externalUsers = useAppSelector(selectManageAccessExternalUsers);
  const hasPendingExternalManualEntries = useAppSelector((state) => state.manageAccess.hasPendingExternalManualEntries);
  const [isExternalConfirmationOpen, setIsExternalConfirmationOpen] = useState(false);

  useEffect(() => {
    if (!isOpen || sixId === null) return;

    const internalUsersEmpty = internalUsers.length === MIN_LEN;
    const externalUsersEmpty = externalUsers.length === MIN_LEN;

    if (internalUsersEmpty || externalUsersEmpty) {
      void dispatch(fetchCreateSixModalInformationThunk())
        .unwrap()
        .then((modalInfo) => {
          dispatch(
            setManageAccessInternalUsers(
              modalInfo.internalUsers.map((internalUser) => ({
                id: internalUser.id,
                name: internalUser.name,
                email: internalUser.email,
                title: internalUser.title,
              }))
            )
          );
          dispatch(
            setManageAccessExternalUsers(
              modalInfo.externalUsers.map((externalUser) => ({
                id: externalUser.id,
                name: externalUser.name,
                email: externalUser.email,
                deptOrg: externalUser.deptOrg,
                title: externalUser.title,
              }))
            )
          );
        });
    }
  }, [dispatch, externalUsers.length, internalUsers.length, isOpen, sixId]);

  const currentWizardStepNumber = resolveManageAccessWizardStepNumber(step);
  const isFirstStep = currentWizardStepNumber === 1;
  const isLastStep = currentWizardStepNumber === MANAGE_ACCESS_WIZARD_STEPS[MANAGE_ACCESS_WIZARD_STEPS.length - 1].stepNumber;
  const isNextDisabled = step === 'Owners' && draft.ownerIds.length === MIN_LEN;
  const nextDisabledTooltip = isNextDisabled ? 'At least one owner is required' : undefined;

  const wizardSteps = MANAGE_ACCESS_WIZARD_STEPS.map((stepMeta) => ({
    stepNumber: stepMeta.stepNumber,
    label: stepMeta.label,
    status: resolveManageAccessWizardStepStatus({
      wizardStepNumber: stepMeta.stepNumber,
      currentWizardStepNumber,
    }),
    isDisabled: stepMeta.stepNumber > currentWizardStepNumber,
  }));

  const handleClose = useCallback((): void => {
    setIsExternalConfirmationOpen(false);
    dispatch(hideModal());
    dispatch(resetManageAccessState());
  }, [dispatch]);

  const handleCloseExternalConfirmation = useCallback((): void => {
    setIsExternalConfirmationOpen(false);
  }, []);

  const handlePrevious = useCallback((): void => {
    if (step === 'Owners') return;
    dispatch(setManageAccessStep(resolvePreviousStep(step)));
  }, [dispatch, step]);

  const handleConfirmExternalConfirmation = useCallback((): void => {
    setIsExternalConfirmationOpen(false);
    dispatch(setManageAccessStep(resolveNextStep('External')));
  }, [dispatch]);

  const handleNext = useCallback((): void => {
    if (step === 'External' && hasPendingExternalManualEntries) {
      setIsExternalConfirmationOpen(true);
      return;
    }

    if (step !== 'Review') {
      dispatch(setManageAccessStep(resolveNextStep(step)));
      return;
    }

    if (sixId === null || sixId.length === 0) return;

    dispatch(setManageAccessSubmitting(true));

    void (async() => {
      try {
        const newExternalUserPayloads = draft.newExternalUsers.map((user) => ({
          name: user.name,
          email: user.email,
          phone: user.phoneNumber,
          departmentOrganization: user.department ?? '',
        }));

        const createdExternalUserIds = await Promise.all(
          newExternalUserPayloads.map(async(user) => await dispatch(createNewExternalUserThunk(user)).unwrap())
        );

        const allExternalParticipantIds = [
          ...new Set([...draft.externalParticipantIds, ...createdExternalUserIds]),
        ];

        await dispatch(updateManageAccessThunk({
          sixId,
          ownerIds: draft.ownerIds,
          internalParticipantIds: draft.internalParticipantIds,
          externalParticipantIds: allExternalParticipantIds,
        })).unwrap();

        showSuccessToast('Access updated successfully');
        dispatch(hideModal());
        dispatch(resetManageAccessState());
      } finally {
        dispatch(setManageAccessSubmitting(false));
      }
    })();
  }, [dispatch, draft, hasPendingExternalManualEntries, sixId, step]);

  return {
    state: {
      isOpen,
      step,
      isSubmitting,
      sixId,
      draft,
      wizardSteps,
      currentWizardStepNumber,
      isFirstStep,
      isLastStep,
      isNextDisabled,
      nextDisabledTooltip,
      isExternalConfirmationOpen,
    },
    handlers: {
      handleClose,
      handleCloseExternalConfirmation,
      handleConfirmExternalConfirmation,
      handlePrevious,
      handleNext,
    },
  };
}
